import { ME_KEY } from '../auth/session';

import type { Me } from '../api/client';
import type { QueryClient } from '@tanstack/react-query';

export type ClinicTheme = NonNullable<Me['theme']>;

/** Último tema aplicado: lo lee public/theme.js antes de que cargue React (sin parpadeo). */
export const THEME_STORAGE_KEY = 'mc.theme';

/**
 * Vista previa (Ajustes › Apariencia): `data-theme` en <html> sin recordarlo. Los tokens de
 * design/tokens.css hacen el resto.
 */
export function previewClinicTheme(theme: ClinicTheme): void {
  const html = document.documentElement;
  html.dataset['theme'] = theme;
  const bg = getComputedStyle(html).getPropertyValue('--bg').trim();
  if (bg !== '') document.querySelector('meta[name="theme-color"]')?.setAttribute('content', bg);
}

/** RF-AJU-10 (ADR-103): aplica el tema de la clínica y lo recuerda para la próxima carga. */
export function applyClinicTheme(theme: ClinicTheme): void {
  previewClinicTheme(theme);
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Sin almacenamiento (modo privado): el tema vale solo para esta carga.
  }
}

/**
 * Sigue a `me`: al entrar, al cambiar de clínica y en cada refetch aplica el tema de la clínica
 * activa. Sin sesión queda el último (la pantalla de login lo usa). Devuelve la desuscripción.
 */
export function watchClinicTheme(queryClient: QueryClient): () => void {
  let applied: ClinicTheme | undefined;
  const apply = (me: Me | null | undefined) => {
    if (me?.theme == null || me.theme === applied) return;
    applied = me.theme;
    applyClinicTheme(me.theme);
  };
  apply(queryClient.getQueryData<Me | null>(ME_KEY));
  return queryClient.getQueryCache().subscribe((event) => {
    const [key] = event.query.queryKey as readonly unknown[];
    if (event.type === 'updated' && key === ME_KEY[0]) apply(event.query.state.data as Me | null | undefined);
  });
}
